/**
 * Novinky (patch notes) — čo sa na portáli a v hrách zmenilo. Píše sa pre
 * hráčov, nie pre vývojárov: čo nové sa dá robiť a čo prestalo zlobiť.
 *
 * Nový záznam ide **na začiatok** poľa. `id` sa nemení — podľa neho si
 * prehliadač pamätá, čo už hráč videl (bodka „nové" v hlavičke).
 */

export type ChangeScope = 'portal' | 'unmatched' | 'dune' | 'karak' | 'mura' | 'outer-rim';

export interface ChangeEntry {
  id: string;
  /** ISO dátum `YYYY-MM-DD`. */
  date: string;
  title: string;
  changes: { scope: ChangeScope; kind: 'new' | 'fix' | 'change'; text: string }[];
}

export const SCOPE_LABEL: Record<ChangeScope, string> = {
  portal: 'Portál',
  unmatched: 'Unmatched',
  dune: 'Dune',
  karak: 'Karak',
  mura: 'Mura',
  'outer-rim': 'Outer Rim',
};

/** Poradie oblastí v patch notes — portál prvý, potom hry. */
export const SCOPES: ChangeScope[] = ['portal', 'unmatched', 'dune', 'karak', 'mura', 'outer-rim'];

export type ScopedEntry = ChangeEntry & { changes: ChangeEntry['changes'] };

/**
 * Patch notes jednej oblasti: záznamy, ktoré sa jej týkajú, a v nich len
 * jej zmeny (záznam „Nová klubovňa" má aj opravu portálu — v Unmatched
 * sa ukáže len časť o Unmatched).
 */
export function entriesFor(scope: ChangeScope): ScopedEntry[] {
  return CHANGELOG.flatMap((entry) => {
    const changes = entry.changes.filter((change) => change.scope === scope);
    return changes.length ? [{ ...entry, changes }] : [];
  });
}

export function isScope(value: string | null | undefined): value is ChangeScope {
  return SCOPES.includes(value as ChangeScope);
}

export const KIND_LABEL: Record<ChangeEntry['changes'][number]['kind'], string> = {
  new: 'Nové',
  fix: 'Oprava',
  change: 'Zmena',
};

export const CHANGELOG: ChangeEntry[] = [
  {
    id: '2026-09-26-mura-grafika',
    date: '2026-09-26',
    title: 'Mura: nová grafika, kryty a hlasy v tme',
    changes: [
      {
        scope: 'mura',
        kind: 'change',
        text: 'Nová pixelartová grafika: kamenné kvádre a tehly, mrežové lávky, väzeň s obojkom a mnísi s bronzovými maskami.',
      },
      {
        scope: 'mura',
        kind: 'new',
        text: 'Nový obraz na titulke hry aj na dlaždici v knižnici — podzemná sieň s klietkami a lúčom svetla zhora.',
      },
      {
        scope: 'mura',
        kind: 'new',
        text: 'Pohľad mníchov sa láme o prekážky — skrč sa za debnu a neuvidia ťa.',
      },
      {
        scope: 'mura',
        kind: 'new',
        text: 'Myšlienky a šepot väzňov v bublinách — najmä na začiatku, keď sa prebudíš v cele.',
      },
      {
        scope: 'mura',
        kind: 'fix',
        text: 'Plamene fakieľ horia pokojnejšie, iskry už nevyletujú ako rakety.',
      },
    ],
  },
  {
    id: '2026-09-26-mura-demo',
    date: '2026-09-26',
    title: 'Nová hra: Mura — Hlas z podzemia (demo)',
    changes: [
      {
        scope: 'mura',
        kind: 'new',
        text: 'Demo novej hry pre jedného hráča: tichá plošinovka so skrývaním a hádankami. Uteč z podzemného kláštora — päť scén, asi 10 minút, bez textu a bez návodu.',
      },
      {
        scope: 'mura',
        kind: 'new',
        text: 'Hrá sa na klávesnici alebo s gamepadom. Postup sa ukladá k účtu pri každom checkpointe, dá sa pokračovať aj na inom počítači.',
      },
    ],
  },
  {
    id: '2026-09-25-verejne-stoly',
    date: '2026-09-25',
    title: 'Verejné stoly a kto je pri stole',
    changes: [
      {
        scope: 'unmatched',
        kind: 'new',
        text: 'Online stôl môže byť verejný: v klubovni ho vidí každý v sekcii „Verejné stoly" a pridať sa môže ktokoľvek, bez odkazu.',
      },
      {
        scope: 'unmatched',
        kind: 'new',
        text: 'V online hre uvidíš, keď sa súper odpojí („nie je pri stole") a keď sa vráti.',
      },
      {
        scope: 'dune',
        kind: 'new',
        text: 'Online stôl môže byť verejný: vidí ho každý v zozname „Verejné stoly" a pridať sa môže ktokoľvek, bez odkazu.',
      },
      {
        scope: 'dune',
        kind: 'new',
        text: 'V online hre uvidíš, keď sa niektorý hráč odpojí („nie je pri stole") a keď sa vráti.',
      },
      {
        scope: 'karak',
        kind: 'new',
        text: 'Online stôl môže byť verejný: vidí ho každý v zozname „Verejné stoly" a pridať sa môže ktokoľvek, bez odkazu.',
      },
    ],
  },
  {
    id: '2026-09-25-karak-regent',
    date: '2026-09-25',
    title: 'Karak 1.1: rozšírenie Regent',
    changes: [
      {
        scope: 'karak',
        kind: 'new',
        text:
          'Rozšírenie Karak: Regent — pri zakladaní stola ho zapneš jedným klikom. Štyria noví hrdinovia (barbar Valduk, bojový mág ' +
          'Barón Markul, hraničiarka Lady Lorraine, akrobatka Hannah), obrie netopiere s tŕňom temnoty a ľadoví mágovia s mrazivou päsťou.',
      },
      {
        scope: 'karak',
        kind: 'new',
        text:
          'Komnaty pána Karaku: v bojovej aréne vyzveš iného hrdinu na súboj (víťaz mu vezme poklad alebo vec), v prekliatej komnate ' +
          'hodíš na kliatbu. Variant Pán Karaku: po piatej komnate jeho duch posadne najslabšieho hrdinu a dá mu schopnosti všetkých ostatných.',
      },
      {
        scope: 'portal',
        kind: 'change',
        text: 'Karak je vo verzii 1.1 — s rozšírením Regent.',
      },
    ],
  },
  {
    id: '2026-09-25-karak-1-0',
    date: '2026-09-25',
    title: 'Karak 1.0',
    changes: [
      {
        scope: 'karak',
        kind: 'change',
        text: 'Základná hra Karak je hotová — plná verzia 1.0.',
      },
    ],
  },
  {
    id: '2026-09-25-karak-odpojenie',
    date: '2026-09-25',
    title: 'Karak: kto odišiel od stola',
    changes: [
      {
        scope: 'karak',
        kind: 'new',
        text:
          'Keď sa pri online stole niekto odpojí, ostatní to uvidia v strede stola aj s tým, či hra naňho čaká. ' +
          'Keď sa vráti, ukáže sa „je späť pri stole". Obyčajný refresh stránky sa neráta.',
      },
    ],
  },
  {
    id: '2026-09-25-karak-mapa',
    date: '2026-09-25',
    title: 'Karak: krajšia a väčšia mapa',
    changes: [
      {
        scope: 'karak',
        kind: 'change',
        text:
          'Mapa ide na celú šírku okna a dlaždice vyzerajú ako tie z krabice — kamenné steny, svetlé chodby, ' +
          'fontány so srdcom a žiariace brány. Príšery aj hrdinovia sú na mape ilustráciami.',
      },
    ],
  },
  {
    id: '2026-09-25-karak-bot-lupicka',
    date: '2026-09-25',
    title: 'Karak: bot sa už nezasekne',
    changes: [
      {
        scope: 'karak',
        kind: 'fix',
        text:
          'Bot za lupičku chodil k nestvore a zas od nej, kolo za kolom. Teraz sa rozhodne naraz — buď bojuje, alebo ' +
          'nestvoru obíde (lupička smie) a ide ďalej. Bot zavretý za nestvorou do nej napokon ide, namiesto čakania.',
      },
    ],
  },
  {
    id: '2026-09-25-karak-suboj',
    date: '2026-09-25',
    title: 'Karak: súboj ako scéna',
    changes: [
      {
        scope: 'karak',
        kind: 'new',
        text:
          'Súboj je teraz scéna: hrdina a nestvora sa proti sebe vyrútia, kockami hádžeš sám tlačidlom, ' +
          'kocky sa kotúľajú a k hodu sa postupne pripočítajú zbrane a kúzla. Víťazstvo, remíza aj prehra majú vlastnú animáciu a ukážu korisť.',
      },
      {
        scope: 'karak',
        kind: 'new',
        text: 'Hrdinovia a nestvory majú ilustrácie z pravidiel a pod kurzorom bublinu s príbehom a schopnosťami.',
      },
      {
        scope: 'karak',
        kind: 'new',
        text: 'Novoobjavená dlaždica sa na dosku „položí" a nestvora či truhlica z nej vyskočí.',
      },
    ],
  },
  {
    id: '2026-09-25-karak',
    date: '2026-09-25',
    title: 'Nová hra: Karak',
    changes: [
      {
        scope: 'portal',
        kind: 'new',
        text: 'Na polici pribudol Karak — dobrodružstvo v katakombách pre 2–5 hráčov.',
      },
      {
        scope: 'karak',
        kind: 'new',
        text:
          'Karak sa dá hrať celý podľa pravidiel: objavovanie katakomb, súboje s kockami, zbrane, kúzla, ' +
          'kľúče a truhlice, kliatba múmie, fontány a teleporty a všetkých šesť hrdinov so schopnosťami. ' +
          'Hrá sa na jednej obrazovke (aj proti botom) alebo online cez pozvánku.',
      },
    ],
  },
  {
    id: '2026-09-25-karty-bez-vodoznaku',
    date: '2026-09-25',
    title: 'Karty bez vodoznaku',
    changes: [
      {
        scope: 'unmatched',
        kind: 'change',
        text: 'Karty Achilla, Sun Wukonga, Yennengy a Nikolu Teslu sú teraz ostré a bez vodoznaku.',
      },
    ],
  },
  {
    id: '2026-09-25-ostrejsie-karty',
    date: '2026-09-25',
    title: 'Ostrejšie karty',
    changes: [
      {
        scope: 'unmatched',
        kind: 'change',
        text:
          'Karty Achilla, Sun Wukonga, Yennengy, Annie Christmas, Golden Bata, Nikolu Teslu a Dr. Jill Trent ' +
          'sú v oveľa vyššom rozlíšení — text aj ilustrácia sú ostré aj pri zväčšení.',
      },
    ],
  },
  {
    id: '2026-09-25-outer-rim-beta',
    date: '2026-09-25',
    title: 'Nová hra: Star Wars: Outer Rim (beta)',
    changes: [
      { scope: 'portal', kind: 'new', text: 'V knižnici pribudla hrateľná beta Star Wars: Outer Rim — klik na obal ju spustí.' },
      { scope: 'outer-rim', kind: 'new', text: 'Celá základná hra podľa pravidiel FFG: 8 postáv s cieľmi, lode, trh, joby, bounty, kontakty, všetky karty stretnutí a databanky, tajomstvá aj sabacc.' },
      { scope: 'outer-rim', kind: 'new', text: 'Hrá sa online s kamarátmi cez odkaz, na jednej obrazovke alebo sólo proti AI pašerákovi podľa oficiálnych sólo pravidiel.' },
      { scope: 'outer-rim', kind: 'new', text: 'Voliteľne dlhšia hra na 12 slávy, láskavosti (favors) so žetónmi dlhu a náhodné poradie dlaždíc mapy.' },
      { scope: 'outer-rim', kind: 'change', text: 'Beta: schopnosti pri hode kockou (prehodenia, jednorazové karty) engine zatiaľ použije sám, keď pomôžu — hráča sa uprostred hodu nepýta.' },
    ],
  },
  {
    id: '2026-09-25-dune-plan-bitky',
    date: '2026-09-25',
    title: 'Dune: čo ukázal súper, tvoji zradcovia',
    changes: [
      { scope: 'dune', kind: 'new', text: 'Keď súper Atreidom ukáže časť plánu, v pláne bitky vidíš obrázok karty (pod kurzorom zväčšený) a čo robí, alebo žetón vodcu so silou a frakciou — nie len meno.' },
      { scope: 'dune', kind: 'new', text: 'Plán bitky ukazuje aj tvojich zradcov; tí, ktorých môže súper v tejto bitke zahrať, sú zvýraznení.' },
    ],
  },
  {
    id: '2026-09-25-dune-animacie',
    date: '2026-09-25',
    title: 'Dune: animácie na mape',
    changes: [
      { scope: 'dune', kind: 'new', text: 'Mapa ožíva: búrka sa otočí cez sektory, ktorými prešla, sily pri výsadku zlietnu na miesto, pri presune a jazde na červovi preletia, korenie z výronu vyskočí, Shai-Hulud rozvlní územie a straty síl zablikajú.' },
      { scope: 'dune', kind: 'change', text: 'Pri prvej búrke je napísané, že sa vytáča vždy — karty búrky ju posúvajú až od 2. kola.' },
    ],
  },
  {
    id: '2026-09-25-dune-1-0',
    date: '2026-09-25',
    title: 'Dune 1.0',
    changes: [
      { scope: 'dune', kind: 'new', text: 'Dune je v plnej verzii 1.0 — celá základná hra podľa pravidiel GF9 so spojenectvami, všetkými kartami a botmi. V knižnici hier to hovorí štítok aj „Aj proti botom".' },
      { scope: 'portal', kind: 'change', text: 'Lístok s patch notes na titulnej stránke je vyšší a ukazuje posledné tri novinky každej hry. Pozadie má veľmi jemnú plátennú textúru.' },
    ],
  },
  {
    id: '2026-09-25-dune-bot',
    date: '2026-09-25',
    title: 'Dune: boti',
    changes: [
      { scope: 'dune', kind: 'change', text: 'Mapa ožila: bronzový rám s ryskami sektorov, jemné textúry (vlnky dún, skaly, murivo pevností, ľad Polar Sink) a reliéf. Arrakeen a Carthag majú značku ornitoptér — kto v nich má sily, presúva sa o 3 územia.' },
      { scope: 'dune', kind: 'new', text: 'Boti v Dune: pri zakladaní stola na jednej obrazovke prepni ktorúkoľvek frakciu na bota — hrá za každú frakciu (draží, vysadzuje, bojuje, uzatvára spojenectvá) a ťahá sám. Stačí jeden človek.' },
    ],
  },
  {
    id: '2026-09-25-dune-karama-vopred',
    date: '2026-09-25',
    title: 'Dune: Karama vopred, šrafovaná búrka',
    changes: [
      { scope: 'dune', kind: 'fix', text: 'Karamu na blokovanie schopnosti smieš zahrať aj vopred a vybrať si fázu tohto kola — napríklad počas búrky alebo CHOAM na dražbu, aby Atreides nevidel ani prvú kartu. Pri každej frakcii je napísané, čo jej Karama v tej fáze vezme.' },
      { scope: 'dune', kind: 'change', text: 'Búrka je na mape šrafovaná a orámovaná, aby bola hneď vidieť.' },
    ],
  },
  {
    id: '2026-09-25-dune-menu-pravidla',
    date: '2026-09-25',
    title: 'Dune: nové menu, nastavenia výhry, obrazovka víťazstva',
    changes: [
      { scope: 'dune', kind: 'change', text: 'Stôl sa zmestí na jednu obrazovku: tvoje karty, zradcovia a vodcovia sú vpravo pod akciami (karty schodíkovito), mapa má celý stred a zvýraznený okraj plochy, ktorou sa dá hýbať.' },
      { scope: 'dune', kind: 'change', text: 'Nové hlavné menu Dune: banner, zakladanie stola po krokoch s výberom frakcií podľa znakov a prehľad stolov.' },
      { scope: 'dune', kind: 'new', text: 'Nastavenia výhry podľa pravidiel: spojenectvo 2, 3 hráčov alebo bez limitu, výhra spojenectva so 4 alebo 3 pevnosťami, búrka kartami alebo kolesami 1–3 a počet kôl.' },
      { scope: 'dune', kind: 'new', text: 'Víťaz sa vyhlási v Mentat Pause veľkou obrazovkou VÍŤAZSTVO so zástavami víťazných frakcií. Nová fáza sa ohlási pásom v strede obrazovky.' },
      { scope: 'dune', kind: 'new', text: 'V navigácii sú oficiálne pravidlá a FAQ v PDF.' },
      { scope: 'dune', kind: 'new', text: 'Fremeni smú po Nexuse jazdiť na červovi. Emperor smie spojencovi zaplatiť až 3 oživenia navyše.' },
      { scope: 'dune', kind: 'fix', text: 'Podľa FAQ pravidiel: Family Atomics sa smie zahrať aj v búrke a vodca oživený Gholou smie bojovať v tých istých bitkách.' },
    ],
  },
  {
    id: '2026-09-24-dune-0-1',
    date: '2026-09-24',
    title: 'Dune 0.1',
    changes: [
      {
        scope: 'dune',
        kind: 'change',
        text:
          'Dune je vo verzii 0.1 — dá sa hrať hráč proti hráčovi (online aj pri jednej obrazovke). ' +
          'Spojenectvá a niektoré karty ešte chýbajú.',
      },
    ],
  },
  {
    id: '2026-09-24-dune-specialne-karty',
    date: '2026-09-24',
    title: 'Dune: Karama a ďalšie špeciálne karty',
    changes: [
      { scope: 'dune', kind: 'new', text: 'Karama: zablokuje schopnosti frakcie do konca fázy (aj Hlas či prescience práve vo chvíli použitia), kúpi draženú kartu zadarmo, alebo dá výsadok za polovicu.' },
      { scope: 'dune', kind: 'new', text: 'Truthtrance: verejná otázka áno/nie — hra počká na odpoveď. Tleilaxu Ghola oživí vodcu alebo 5 síl zadarmo, Hajr dá presun navyše.' },
      { scope: 'dune', kind: 'new', text: 'Weather Control a Family Atomics: keď ich niekto drží, búrka od 2. kola počká, kým sa každý rozhodne. Po Family Atomics búrka zasahuje aj Arrakeen, Carthag a Imperial Basin.' },
    ],
  },
  {
    id: '2026-09-24-dune-frakcie-mapa',
    date: '2026-09-24',
    title: 'Dune: čo vedia frakcie, širšia mapa',
    changes: [
      { scope: 'dune', kind: 'new', text: 'Podrž kurzor na mene frakcie (súperi, tvoj dok, pás „na rade", výber frakcií) — ukáže sa, čo frakcia vie a čo dáva spojencovi.' },
      { scope: 'dune', kind: 'change', text: 'Mapa zaberá celú šírku stola a dá sa posúvať aj do strán.' },
    ],
  },
  {
    id: '2026-09-24-dune-spojenectva',
    date: '2026-09-24',
    title: 'Dune: spojenectvá',
    changes: [
      { scope: 'dune', kind: 'new', text: 'Spojenectvá: keď od 2. kola padne Shai-Hulud, nastane Nexus a hráči si môžu navrhnúť, prijať, odmietnuť alebo opustiť spojenectvo (v hre aspoň troch).' },
      { scope: 'dune', kind: 'new', text: 'Spojenci vyhrávajú spolu so 4 pevnosťami, nebojujú proti sebe a nevstupujú do územia toho druhého (okrem Polar Sink). Korenie si môžu posielať počas dražby a výsadku, Emperor kedykoľvek.' },
      { scope: 'dune', kind: 'new', text: 'Výhody pre spojenca podľa frakcie: Hlas BG, prescience Atreidov, zradcovia Harkonnena, polovičné výsadky Guildy, ochrana pred červom a 3 oživenia zadarmo od Fremenov.' },
    ],
  },
  {
    id: '2026-09-24-dune-stol-na-mape',
    date: '2026-09-24',
    title: 'Dune: hra na mape',
    changes: [
      { scope: 'dune', kind: 'change', text: 'Nové rozloženie stola: súperi vľavo, mapa v strede, akcie vpravo, tvoje karty a obchod dole. Priebeh sa otvára tlačidlom v hlavičke.' },
      { scope: 'dune', kind: 'new', text: 'Výsadok, presun aj rozostavenie Fremenov sa robia na mape: nastav počet, klikni na územie a potvrď v bubline.' },
      { scope: 'dune', kind: 'new', text: 'Na mape sú názvy území. Mapa sa dá posúvať ťahaním a priblížiť kolieskom, dvojklikom alebo tlačidlami +/−.' },
      { scope: 'dune', kind: 'change', text: 'Pri výsadku a presune svieti celé územie v dosahu a klikne sa na guličku sektora, kam chceš ísť — v území v dosahu na ktorýkoľvek sektor mimo búrky.' },
      { scope: 'dune', kind: 'change', text: 'Stôl je na celú šírku okna a mapa je väčšia.' },
      { scope: 'dune', kind: 'fix', text: 'Habbanya Ridge Flat a Bight of the Cliff mali bod pre sily vnútri susedného mesta — sú oddelené. Ciele výsadku v mestách sú zelené.' },
      { scope: 'dune', kind: 'fix', text: 'Habbanya Sietch je na mape znova vidieť ako pevnosť. Pevnosti majú výraznejšiu farbu.' },
      { scope: 'dune', kind: 'new', text: 'Počas dražby sa namiesto mapy ukážu dražené karty vedľa seba — kto čo kúpil a za koľko, aktuálna ponuka a kto je na rade. Atreides pod draženou kartou rovno predáva informáciu o nej.' },
      { scope: 'dune', kind: 'new', text: 'Pohrebisko (Tleilaxu Tanks) v hlavičke: koľko síl a ktorí vodcovia sú mŕtvi, pri každej frakcii.' },
      { scope: 'dune', kind: 'change', text: 'Od 2. kola posúva búrku karta búrky. Fremeni vidia ďalšiu kartu vopred.' },
      { scope: 'dune', kind: 'fix', text: 'Karty sa pod kurzorom zväčšia na dobre čitateľnú veľkosť a v ostrom rozlíšení.' },
    ],
  },
  {
    id: '2026-09-24-unmatched-1-0',
    date: '2026-09-24',
    title: 'Unmatched 1.0',
    changes: [
      {
        scope: 'unmatched',
        kind: 'new',
        text: 'Unmatched je v plnej verzii 1.0 — v knižnici hier to hovorí aj štítok na jeho karte.',
      },
      {
        scope: 'portal',
        kind: 'change',
        text:
          'Každá hra má v knižnici názov vo svojom písme a texty na kartách sú jasnejšie a lepšie čitateľné.',
      },
    ],
  },
  {
    id: '2026-09-24-ghostrider-arthur-willow',
    date: '2026-09-24',
    title: 'Ghost Rider, King Arthur a Willow',
    changes: [
      {
        scope: 'unmatched',
        kind: 'new',
        text:
          'Hrateľní sú Ghost Rider, King Arthur (s Merlinom) a Willow (s Tarou) — všetky karty s efektmi. ' +
          'Ghost Rider míňa Hellfire na jazdu ponad súperov a silnejšie karty, King Arthur smie boostovať každý ' +
          'svoj útok a Willow sa po zranení mení na Dark Willow.',
      },
      {
        scope: 'unmatched',
        kind: 'fix',
        text: 'Feint (a iné zrušenia) teraz zruší aj útočnú kartu, ktorá sama žiadny efekt nemá.',
      },
    ],
  },
  {
    id: '2026-09-24-pravidla-pdf',
    date: '2026-09-24',
    title: 'Pravidlá v PDF',
    changes: [
      {
        scope: 'unmatched',
        kind: 'new',
        text: 'V hornej lište Unmatched pribudli Pravidlá — oficiálne Unmatched Core Rules v PDF sa otvoria v novej karte.',
      },
    ],
  },
  {
    id: '2026-09-24-kniznica-hier',
    date: '2026-09-24',
    title: 'Knižnica hier a patch notes',
    changes: [
      {
        scope: 'portal',
        kind: 'change',
        text:
          'Titulná stránka je knižnica hier: každá hra má kartu s obalom, počtom hráčov, dĺžkou a štítkami, ' +
          'pri Unmatched aj tvoje rozohrané stoly — vrátane toho, kde si na ťahu.',
      },
      {
        scope: 'portal',
        kind: 'new',
        text: 'Na polici pribudli Star Wars: Outer Rim a Scythe — zatiaľ ako pripravované, hrať sa ešte nedajú.',
      },
      {
        scope: 'portal',
        kind: 'change',
        text: 'Karta Unmatched v knižnici má na obale oficiálne logo hry.',
      },
      {
        scope: 'portal',
        kind: 'change',
        text: 'Dune, Star Wars: Outer Rim a Scythe majú v knižnici obaly z krabíc.',
      },
      {
        scope: 'portal',
        kind: 'change',
        text:
          'Novinky sa volajú Patch notes a sú rozdelené podľa portálu a jednotlivých hier — prehľad, zmeny ' +
          'jednej hry aj celá história.',
      },
    ],
  },
  {
    id: '2026-09-23-sherlock-hamlet-wukong',
    date: '2026-09-23',
    title: 'Sherlock Holmes, Hamlet a Sun Wukong',
    changes: [
      {
        scope: 'unmatched',
        kind: 'new',
        text:
          'Hrateľní sú Sherlock Holmes (s Dr. Watsonom), Hamlet (s Rosencrantzom a Guildensternom) a Sun Wukong ' +
          '— všetky karty s efektmi. Holmesove karty sa nedajú zrušiť a Elementary tipuje hodnotu útoku ešte ' +
          'pred odhalením, Hamlet si každý ťah vyberá byť, či nebyť, a Sun Wukong vyvoláva klony.',
      },
    ],
  },
  {
    id: '2026-09-23-hearth-and-table',
    date: '2026-09-23',
    title: 'Hearth & Table',
    changes: [
      {
        scope: 'portal',
        kind: 'new',
        text:
          'Herňa má meno a logo — Hearth & Table. Titulná stránka aj prihlásenie dostali farby loga: ' +
          'šero pri ohni, svetlo lampy a pahreba, ktorá jemne dýcha.',
      },
    ],
  },
  {
    id: '2026-09-23-utulny-stol',
    date: '2026-09-23',
    title: 'Útulnejší portál',
    changes: [
      {
        scope: 'portal',
        kind: 'change',
        text:
          'Titulná stránka s hrami má nový vzhľad: drevený stôl pod lampou, hry na ňom ležia ako krabice ' +
          'a novinky ako pripnutý lístok. Každá hra si ponechala svoj vlastný štýl.',
      },
      {
        scope: 'unmatched',
        kind: 'change',
        text: 'Klubovňa Unmatched dostala pozadie arény — teplé svetlo zhora a jemná sieť priestorov ako na doske.',
      },
    ],
  },
  {
    id: '2026-09-23-klubovna',
    date: '2026-09-23',
    title: 'Nová klubovňa',
    changes: [
      {
        scope: 'unmatched',
        kind: 'change',
        text:
          'Klubovňa je prehľadnejšia: navrchu banner s dvoma postavami a tlačidlom Založiť stôl (a odkazom na ' +
          'hru proti botovi), pod ním stoly, kde si na ťahu (s tlačidlom Hrať), potom tri ' +
          'spôsoby, ako začať novú hru (proti botovi, s kamarátom online, na jednom počítači), a potom stoly, ' +
          'kde sa čaká na súpera — pozvánku skopíruješ jedným klikom priamo zo zoznamu.',
      },
      {
        scope: 'unmatched',
        kind: 'fix',
        text: 'Zoznam stolov posielal do prehliadača viac údajov, než mal — opravené.',
      },
    ],
  },
  {
    id: '2026-09-23-strange-jekyll-tesla',
    date: '2026-09-23',
    title: 'Doctor Strange, Jekyll & Hyde a Nikola Tesla',
    changes: [
      {
        scope: 'unmatched',
        kind: 'new',
        text:
          'Hrateľní sú Doctor Strange (s Wongom), Jekyll & Hyde a Nikola Tesla — všetky karty s efektmi. ' +
          'Strange si po súboji smie kartu vymeniť za 1 zdravie a boostuje naslepo z balíčka, Jekyll & Hyde sa ' +
          'na začiatku ťahu premieňa a každá forma smie hrať len svoje karty, Tesla nabíja a vybíja dve cievky ' +
          '(stav vidno na karte hráča).',
      },
    ],
  },
  {
    id: '2026-09-23-spider-shehulk-tomoe',
    date: '2026-09-23',
    title: 'Spider-Man, She-Hulk a Tomoe Gozen',
    changes: [
      {
        scope: 'unmatched',
        kind: 'new',
        text:
          'Hrateľní sú Spider-Man, She-Hulk a Tomoe Gozen — všetky karty s efektmi. Kto útočí na Spider-Mana, ' +
          'musí vopred ohlásiť hodnotu útoku (ukáže sa v súbojovej fáze), She-Hulk na začiatku ťahu hádže karty ' +
          'po figúrkach vo svojej zóne a súperov hrdina, ktorý opustí zónu Tomoe, dostane 1 poškodenie.',
      },
    ],
  },
  {
    id: '2026-09-23-bullseye-genie-winter',
    date: '2026-09-23',
    title: 'Bullseye, The Genie a Winter Soldier',
    changes: [
      {
        scope: 'unmatched',
        kind: 'new',
        text:
          'Hrateľní sú Bullseye, The Genie a Winter Soldier — všetky karty s efektmi. Bullseye strieľa až na ' +
          '5 priestorov a jeho karty silnejú po vyhratom súboji, Génius si na začiatku ťahu kupuje akcie kartami ' +
          'a Winter Soldierovi sa efekty kariet nedajú zrušiť.',
      },
      {
        scope: 'unmatched',
        kind: 'fix',
        text:
          'Like a Flash of Golden Light (Golden Bat) a Jump Scare (Bloody Mary) mali vyššiu hodnotu, aj keď sa ' +
          'figúrka v ťahu vôbec nepohla.',
      },
    ],
  },
  {
    id: '2026-09-23-goldenbat-mary-marvel',
    date: '2026-09-23',
    title: 'Golden Bat, Bloody Mary a Ms. Marvel',
    changes: [
      {
        scope: 'unmatched',
        kind: 'new',
        text:
          'Hrateľní sú Golden Bat, Bloody Mary a Ms. Marvel — všetky karty s efektmi. Golden Bat útočí o 2 silnejšie, ' +
          'kým v ťahu nemanévroval, Bloody Mary s presne 3 kartami v ruke získa tretiu akciu a Ms. Marvel sa na ' +
          'začiatku ťahu smie natiahnuť o priestor a útočí až na 2 priestory.',
      },
    ],
  },
  {
    id: '2026-09-23-dracula-luke-bruce',
    date: '2026-09-23',
    title: 'Dracula, Luke Cage a Bruce Lee',
    changes: [
      {
        scope: 'unmatched',
        kind: 'new',
        text:
          'Hrateľní sú Dracula, Luke Cage a Bruce Lee — všetky karty s efektmi. Dracula na začiatku ťahu hryzie ' +
          'susedov, Luke dostáva zo súboja o 2 menej a Bruce Lee reťazí útoky JEET KUNE DO za akcie navyše.',
      },
    ],
  },
  {
    id: '2026-09-23-philippa-robin-trex',
    date: '2026-09-23',
    title: 'Philippa, Robin Hood a T. Rex',
    changes: [
      {
        scope: 'unmatched',
        kind: 'new',
        text:
          'Hrateľní sú Philippa, Robin Hood a T. Rex — všetky karty s efektmi. Philippa si na konci ťahu dotiahne ' +
          'ruku do 4, Robin sa po útoku smie pohnúť o 2, T. Rex útočí aj na 2 priestory a na konci ťahu ťahá kartu.',
      },
    ],
  },
  {
    id: '2026-09-23-annie-beowulf-panther',
    date: '2026-09-23',
    title: 'Annie Christmas, Beowulf a Black Panther',
    changes: [
      {
        scope: 'unmatched',
        kind: 'new',
        text:
          'Hrateľní sú Annie Christmas, Beowulf a Black Panther — všetky karty s efektmi. Beowulf zbiera ' +
          'Rage (vidno ho na lište), Black Panther ukladá súperove karty do Vibranium Suit a boostuje nimi.',
      },
      {
        scope: 'unmatched',
        kind: 'fix',
        text: 'Keď zápas skončil uprostred súboja, karty zo súboja sa stratili. Teraz idú do odhodu.',
      },
    ],
  },
  {
    id: '2026-09-23-alice-leshen-angel',
    date: '2026-09-23',
    title: 'Alice, Ancient Leshen a Angel',
    changes: [
      {
        scope: 'unmatched',
        kind: 'new',
        text:
          'Hrateľní sú Alice, Ancient Leshen a Angel — všetky karty s efektmi. Alice si na začiatku ' +
          'vyberá veľkosť (veľká +2 k útoku, malá +1 k obrane) a karty ju menia; aktuálna veľkosť je na lište.',
      },
      {
        scope: 'unmatched',
        kind: 'new',
        text:
          'Leshen privoláva Vlkov (pohyb 3) a jeho druhý útok v ťahu má +3. Angel si pri prehranom útoku potiahne kartu.',
      },
    ],
  },
  {
    id: '2026-09-22-matches',
    date: '2026-09-22',
    title: 'Zoznam všetkých zápasov',
    changes: [
      {
        scope: 'unmatched',
        kind: 'new',
        text:
          'V menu pribudli „Zápasy": všetky zápasy všetkých hráčov — online, proti botovi aj hot-seat — ' +
          's filtrom podľa režimu a stavu. Kto s kým hrá, na akej mape a kto vyhral. Otvoriť môžeš svoje.',
      },
      {
        scope: 'portal',
        kind: 'fix',
        text: 'Hlavička a knižnica kariet už na mobile nepretekajú do strany.',
      },
    ],
  },
  {
    id: '2026-09-22-side-players',
    date: '2026-09-22',
    title: 'Hráči po stranách dosky',
    changes: [
      {
        scope: 'unmatched',
        kind: 'change',
        text:
          'Tvoja postava a jej štatistiky (zdravie, ruka, balík, odhod) sú vľavo od dosky, súper presne oproti ' +
          'vpravo. Doska je vyššia — pás hráčov nad ňou zmizol.',
      },
      {
        scope: 'unmatched',
        kind: 'change',
        text: 'Priebeh zápasu je skrytý — ukáže ho tlačidlo „Priebeh" v pravom hornom rohu dosky.',
      },
      {
        scope: 'unmatched',
        kind: 'new',
        text: 'Pri prezeraní odhodu sa karta pod kurzorom zväčší, rovnako ako v ruke.',
      },
    ],
  },
  {
    id: '2026-09-22-boards',
    date: '2026-09-22',
    title: 'Päť skutočných dosiek',
    changes: [
      {
        scope: 'unmatched',
        kind: 'new',
        text:
          'Nové arény podľa skutočných dosiek: Marmoreal, Sarpedon, Baskerville Manor, Fayrlund Forest ' +
          'a Streets of Novigrad — s obrázkom dosky, zónami, čiarami aj štartmi.',
      },
      {
        scope: 'unmatched',
        kind: 'new',
        text: 'Baskerville Manor má tajné chodby (kľúčová dierka): dá sa nimi prejsť, ale nie cez ne útočiť.',
      },
      {
        scope: 'unmatched',
        kind: 'new',
        text:
          'Na začiatku hry si sám rozostavíš sidekickov v zóne svojho hrdinu — klik na priestor, ' +
          'klik na iného vlastného sidekicka ich vymení, alebo „Nechať, kde stojí".',
      },
    ],
  },
  {
    id: '2026-09-22-witcher',
    date: '2026-09-22',
    title: 'Ciri, Eredin a Yennefer & Triss',
    changes: [
      {
        scope: 'unmatched',
        kind: 'new',
        text:
          'Hrateľní sú Ciri, Eredin a Yennefer & Triss zo sád The Witcher — všetky karty s efektmi, ' +
          'podľa oficiálnych pravidiel sád.',
      },
      {
        scope: 'unmatched',
        kind: 'new',
        text:
          'Ciri: sila kariet rastie so Source kartami 🔵 v odhode — počet je vidieť na lište hráča. ' +
          'Eredin zúri (ENRAGED), keď padnú všetci Red Riders. Pri Yennefer si na začiatku hry vyberáš, ' +
          'či bude hrdinkou ona, alebo Triss.',
      },
      {
        scope: 'unmatched',
        kind: 'new',
        text: 'Trvalé schémy (ONGOING) ostávajú ležať pred hráčom a sú vidieť na jeho lište.',
      },
    ],
  },
  {
    id: '2026-09-22-library-heroes',
    date: '2026-09-22',
    title: 'Väčší výber hrdinov v knižnici',
    changes: [
      {
        scope: 'unmatched',
        kind: 'change',
        text:
          'Výber hrdinov v knižnici kariet je panel s mriežkou väčších dlaždíc namiesto úzkeho pásu — ' +
          'naraz vidno asi 20 postáv, dlhé mená sa zalomia a hľadanie ukáže, koľko postáv mu zodpovedá.',
      },
    ],
  },
  {
    id: '2026-09-22-boost-confirm',
    date: '2026-09-22',
    title: 'Potvrdenie odhodenia a WIN',
    changes: [
      {
        scope: 'unmatched',
        kind: 'change',
        text:
          'Každé odhodenie karty z ruky sa najprv potvrdzuje — za boost (manéver aj súboj), ' +
          'na limit ruky aj kvôli efektu. Nepozorný klik ťa už nepripraví o kartu. Enter potvrdí, Esc zruší.',
      },
      {
        scope: 'unmatched',
        kind: 'new',
        text:
          'Koniec zápasu má veľký nápis WIN v strede dosky (porazený v online hre a proti botovi vidí DEFEAT). ' +
          'Dá sa zavrieť a pozrieť si dosku.',
      },
    ],
  },
  {
    id: '2026-09-22-bot',
    date: '2026-09-22',
    title: 'Hra proti botovi',
    changes: [
      {
        scope: 'unmatched',
        kind: 'new',
        text:
          'Singleplayer: pri novom stole vyber „Proti botovi" a obtiažnosť Ľahký, Stredný alebo Ťažký. ' +
          'Postavu vyberáš sebe aj botovi.',
      },
      {
        scope: 'unmatched',
        kind: 'new',
        text:
          'Ťažký bot premýšľa dopredu a odhaduje tvoje karty z odhodu. Tvoju ruku nevidí — ' +
          'dostáva presne to, čo by videl človek.',
      },
      {
        scope: 'unmatched',
        kind: 'change',
        text: 'Výhry nad botom sa do bilancie v klubovni nerátajú.',
      },
      {
        scope: 'unmatched',
        kind: 'fix',
        text: 'Keď súper útočil a ty si sa bránil, hlavička hlásila „tvoj ťah". Už ukazuje, kto je naozaj na ťahu.',
      },
      {
        scope: 'portal',
        kind: 'new',
        text: 'Táto stránka s novinkami.',
      },
    ],
  },
  {
    id: '2026-09-22-portal',
    date: '2026-09-22',
    title: 'Portál Tabletop a Dune',
    changes: [
      {
        scope: 'portal',
        kind: 'new',
        text:
          'tabletop.dustbox.app je portál pre viac hier. Registrácia je s pozývacím kódom, ' +
          'potom sa prihlasuješ menom a heslom.',
      },
      {
        scope: 'dune',
        kind: 'new',
        text:
          'Dune (WIP): základná hra sa dá hrať v prehliadači, na jednej obrazovke aj online. ' +
          'Chýbajú spojenectvá a efekty niektorých kariet.',
      },
      {
        scope: 'unmatched',
        kind: 'change',
        text: 'Unmatched sa presunul pod #/unmatched. Staré odkazy na stoly fungujú ďalej.',
      },
    ],
  },
  {
    id: '2026-09-22-heroes',
    date: '2026-09-22',
    title: 'Päť nových postáv',
    changes: [
      {
        scope: 'unmatched',
        kind: 'new',
        text: 'Hrateľní sú Achilles, Invisible Man, Medusa, Oda Nobunaga a Houdini — všetky karty so svojimi efektmi.',
      },
      {
        scope: 'unmatched',
        kind: 'new',
        text: 'Invisible Man: žetóny hmly na doske, skoky medzi nimi a Vanish mimo dosky.',
      },
      {
        scope: 'unmatched',
        kind: 'fix',
        text: 'Na priestor s hmlou sa dá postaviť a hmla je vidieť aj pod figúrkou.',
      },
    ],
  },
  {
    id: '2026-09-21-design',
    date: '2026-09-21',
    title: 'Nový vzhľad',
    changes: [
      {
        scope: 'unmatched',
        kind: 'change',
        text: 'Nový vzhľad klubovne, výberu bojovníkov, knižnice kariet aj samotnej hry.',
      },
      {
        scope: 'unmatched',
        kind: 'fix',
        text: 'Portréty postáv vo výbere už nie sú stlačené.',
      },
    ],
  },
  {
    id: '2026-09-10-start',
    date: '2026-09-10',
    title: 'Unmatched v prehliadači',
    changes: [
      {
        scope: 'unmatched',
        kind: 'new',
        text: 'Online hra cez pozývací odkaz aj hot-seat na jednej obrazovke.',
      },
      {
        scope: 'unmatched',
        kind: 'new',
        text: 'Bigfoot a Sinbad s efektmi kariet, tri arény a knižnica kariet.',
      },
    ],
  },
];

const SEEN_KEY = 'tabletop:changelog:seen';

/** Má hráč novinky, ktoré ešte nevidel? (Kto stránku ešte neotvoril, má všetky.) */
export function hasUnseen(): boolean {
  try {
    return localStorage.getItem(SEEN_KEY) !== (CHANGELOG[0]?.id ?? '');
  } catch {
    return false;
  }
}

export function markSeen(): void {
  try {
    localStorage.setItem(SEEN_KEY, CHANGELOG[0]?.id ?? '');
  } catch {
    // Bez úložiska sa bodka jednoducho neukáže.
  }
}

export function formatDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return `${d}. ${m}. ${y}`;
}
